import type { Feature } from '@/app/components/search/components/Card/utils/facilitiesTypes.ts';
import { waitingFlightItem, waitingItem } from '@/app/components/search/components/Card/utils/facilitiesTypes.ts';
import { getParams } from '@/app/components/search/components/Card/Details/Features/getFeatures/getFeaturesParams.ts';
import type { GetFeaturesParams } from '@/app/components/search/components/Card/Details/Features/getFeatures/types.ts';
import { getTransportFeatures } from '@/app/components/search/components/Card/Details/Features/getFeatures/utils/getTransportFeatures.tsx';
import { getFacilitiesFeatures } from '@/app/components/search/components/Card/Details/Features/getFeatures/utils/getFacilitiesFeatures.tsx';
import { getCancellationFeature } from '@/app/components/search/components/Card/Details/Features/getFeatures/utils/getCancellationFeature.tsx';
import { getFranceUSPFeature } from '@/app/components/search/components/Card/Details/Features/getFeatures/utils/getFranceUSPFeature.tsx';
import { getPromotionFeatures } from '@/app/components/search/components/Card/Details/Features/getFeatures/utils/getPromotionFeatures.tsx';
import { getPackageSavingsFeature } from '@/app/components/search/components/Card/Details/Features/getFeatures/utils/getPackageSavingsFeature.tsx';
import { getTravelExtraPackFeature } from '@/app/components/search/components/Card/Details/Features/getFeatures/utils/getTravelExtraPackFeature.tsx';
// eslint-disable-next-line import/no-cycle
import { getDateFeatures } from '@/app/components/search/components/Card/Details/Features/getFeatures/utils/getDateFeatures.tsx';
import { getSoldOutFeatures } from '@/app/components/search/components/Card/Details/Features/getFeatures/utils/getSoldOutFeatures.tsx';
import { getDepositFeature } from '@/app/components/search/components/Card/Details/Features/getFeatures/utils/getDepositFeatures.tsx';
import { getMealFeatures } from '@/app/components/search/components/Card/Details/Features/getFeatures/utils/getMealFeatures.tsx';
import { getVoucherFeature } from '@/app/components/search/components/Card/Details/Features/getFeatures/utils/getVoucherFeature.tsx';

type GetFeaturesOutput = {
  dates?: Feature[];
  features?: Feature[];
  inlineFeatures?: Feature[];
  transportFeatures?: Feature[];
};

export const getFeatures = (props: GetFeaturesParams): GetFeaturesOutput => {
  const params = getParams(props);
  const { isDP, isTH, isHO, isLoading, isWaiting, isOverlay, soldOut, dealCard } = params;
  const hasAccommodationData = dealCard?.product?.accommodation;
  const showLoaders = isLoading || isWaiting || !hasAccommodationData;
  const isPackage = isDP || isTH;

  // when the card is loading, we show loaders instead of USPs
  if (showLoaders) {
    return {
      features: isOverlay ? [] : [waitingItem, waitingItem, waitingItem],
      transportFeatures: isDP ? [waitingFlightItem, waitingFlightItem, waitingFlightItem] : [],
    };
  }

  // when the deal is sold out, we show only the sold out USP
  if (soldOut) {
    return {
      features: getSoldOutFeatures(params),
    };
  }

  return {
    // dates are shown as the first USP in the card
    dates: [...getDateFeatures(params)],

    // these USPs are shown horizontally in the card (by design only shown in HO)
    inlineFeatures: [...(isHO ? getFacilitiesFeatures(params) : [])],

    // these USPs are shown vertically in the card
    features: [
      ...(isHO ? getMealFeatures(params) : []),
      ...(isPackage && !isOverlay ? [...getTravelExtraPackFeature(params), ...getPackageSavingsFeature(params)] : []),
      ...(!isOverlay ? getCancellationFeature(params) : []),
      // ...getDistanceFeatures(params),
      ...getPromotionFeatures(params),
      ...(isPackage && isOverlay ? [...getTransportFeatures(params)] : []),
      ...(isPackage && !isOverlay ? [...getFacilitiesFeatures(params)] : []),
      ...(!isOverlay ? [...getDepositFeature(params)] : []),
      ...getFranceUSPFeature(params),
      ...(!isOverlay ? getVoucherFeature(params) : []),
    ],

    // these USPs are shown inside a gray panel in the card
    transportFeatures: [...(isPackage && !isOverlay ? [...getTransportFeatures(params)] : [])],
  };
};
